/**
 * <CompanyInfo> — 기업 정보 위젯
 *
 * 레이아웃 (숨 쉬는 구조):
 *   [logo 40px]  TICKER
 *                Apple Inc.
 *
 *   $266.17                  ╱╲  ╱
 *   ▼ −2.16%               ╱  ╲╱
 *
 *   52주
 *   $190 ────●──────── $289
 *
 *   [썸네일]  제목 (2줄)
 *             Motley Fool · 3시간 전
 *
 *   시총 $4T · 거래량 49.5M
 *
 * 데이터: GET /api/ticker/company/:symbol
 *   - Polygon snapshot + details + aggs + News API
 *   - 뉴스는 Polygon News (공식 publisher · 썸네일 · UTC 발행일 · sentiment)
 */
"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { TrendingDown, TrendingUp, Minus, Repeat2, Search, X, ArrowUpRight } from "lucide-react";
import type { WidgetMeta, WidgetProps } from "../types";
import { api } from "@/lib/api";
import { cleanCompanyName } from "@/lib/clean-company-name";
import { koreanCompanyName } from "@/lib/korean-company-name";

interface InsightSource {
  name: string;
  url: string | null;
  publishedAt: string | null;
}
interface Insight {
  title: string;
  body: string;
  sources: InsightSource[];
}

interface CompanyData {
  ticker: string;
  name: string | null;
  sector: string | null;
  marketCap: number | null;
  logoUrl: string | null;
  price: number | null;
  previousClose: number | null;
  changePercent: number | null;
  change: number | null;
  volume: number | null;
  high52w: number | null;
  low52w: number | null;
  sparkline: number[];
  primaryExchange: string | null;
  brief: { insights: Insight[] } | null;
  stale?: boolean; // true = 장 시작 전/마감 후 · changePercent 는 "전일 실제 변동률"
}

const REFRESH_MS = 60 * 1000;

function formatPrice(v: number | null): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
}

/** 한국 기준 상대 시간 ("3시간 전"). tooltip 에 절대 KST 포함 */
function formatRelativeKST(iso: string | null): { label: string; absolute: string } {
  if (!iso) return { label: "", absolute: "" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { label: "", absolute: "" };
  const ms = Date.now() - d.getTime();
  const minutes = Math.floor(ms / 60000);
  let label: string;
  if (minutes < 1) label = "방금";
  else if (minutes < 60) label = `${minutes}분 전`;
  else if (minutes < 60 * 24) label = `${Math.floor(minutes / 60)}시간 전`;
  else if (minutes < 60 * 24 * 7) label = `${Math.floor(minutes / (60 * 24))}일 전`;
  else label = d.toLocaleDateString("ko-KR", { timeZone: "Asia/Seoul", month: "numeric", day: "numeric" });

  const absolute = d.toLocaleString("ko-KR", {
    timeZone: "Asia/Seoul",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return { label, absolute: `${absolute} (KST)` };
}

/** AI 브리핑 — 이슈 1~3개 (제목 + 우측 시간 + 본문 · 출처 제거) */
function Insights({ insights, loading }: { insights: Insight[]; loading: boolean }) {
  if (loading && insights.length === 0) {
    return (
      <div className="dw-ci-insights">
        {[0, 1, 2].map((i) => (
          <div key={i} className="dw-ci-insight">
            <span className="bs-skel" style={{ width: "70%", height: 12, display: "block", marginBottom: 6 }} />
            <span className="bs-skel" style={{ width: "100%", height: 10, display: "block" }} />
          </div>
        ))}
      </div>
    );
  }
  if (insights.length === 0) {
    return <div className="dw-ci-news-empty">최근 이슈 준비중</div>;
  }
  return (
    <div className="dw-ci-insights">
      {insights.slice(0, 3).map((ins, i) => {
        const mainSource = ins.sources?.[0];
        const { label: timeLabel, absolute: timeAbs } = formatRelativeKST(
          mainSource?.publishedAt ?? null
        );
        const titleInner = (
          <>
            <span className="dw-ci-insight-title">{ins.title}</span>
            {timeLabel && (
              <span className="dw-ci-insight-time" title={timeAbs}>{timeLabel}</span>
            )}
          </>
        );
        return (
          <article key={i} className="dw-ci-insight">
            {mainSource?.url ? (
              <a
                href={mainSource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="dw-ci-insight-head dw-ci-insight-link"
              >
                {titleInner}
              </a>
            ) : (
              <div className="dw-ci-insight-head">{titleInner}</div>
            )}
            {ins.body && <p className="dw-ci-insight-body">{ins.body}</p>}
          </article>
        );
      })}
    </div>
  );
}

/** 미니 스파크라인 SVG */
function Sparkline({ values, up }: { values: number[]; up: boolean }) {
  if (!values || values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const W = 100;
  const H = 30;
  const coords = values.map((v, i) => {
    const x = (i / (values.length - 1)) * W;
    const y = H - ((v - min) / range) * H;
    return [x, y] as [number, number];
  });
  const points = coords.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
  const areaPoints = `0,${H} ${points} ${W},${H}`;
  const color = up ? "var(--bs-up)" : "var(--bs-down)";
  return (
    <svg
      className="dw-ci-spark"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="dw-ci-spark-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill="url(#dw-ci-spark-grad)" />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/** 종목 검색/교체 — 카드 내부에서 자동완성 + Enter 시 존재하는 종목만 확정 */
interface TickerItem {
  symbol: string;
  name?: string;
  market?: string;
}
function TickerPicker({
  current,
  onSelect,
  onCancel,
}: {
  current: string;
  onSelect: (symbol: string) => void;
  onCancel: () => void;
}) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<TickerItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [focusIdx, setFocusIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const fetchItems = useCallback(async (q: string) => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      setLoading(true);
      const data = await api<{ items?: TickerItem[] }>(
        `/api/tickers?q=${encodeURIComponent(q)}&mode=ac`,
        { signal: ctrl.signal }
      );
      setItems(Array.isArray(data?.items) ? data.items.slice(0, 6) : []);
      setFocusIdx(0);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInput = (v: string) => {
    setQuery(v);
    if (timerRef.current) clearTimeout(timerRef.current);
    const trimmed = v.trim();
    if (trimmed.length < 1) {
      setItems([]);
      return;
    }
    timerRef.current = setTimeout(() => fetchItems(trimmed), 220);
  };

  const confirm = (symbolOverride?: string) => {
    if (symbolOverride) {
      onSelect(symbolOverride.toUpperCase());
      return;
    }
    // 입력값이 결과 중 하나와 exact match (symbol) 이면 그걸로
    const q = query.trim().toUpperCase();
    const exact = items.find((x) => x.symbol.toUpperCase() === q);
    if (exact) { onSelect(exact.symbol.toUpperCase()); return; }
    // 아니면 현재 포커스된 결과로 확정 (실제 존재 종목만)
    const target = items[focusIdx];
    if (target) { onSelect(target.symbol.toUpperCase()); return; }
    // 매칭 없음 → 차단 (입력 유지, 사용자가 더 입력하거나 Esc)
  };

  return (
    <div className="dw-body dw-ci dw-ci-picker">
      <div className="dw-ci-picker-head">
        <span className="dw-ci-picker-label">종목 검색</span>
        <button
          type="button"
          className="dw-ci-picker-cancel"
          onClick={onCancel}
          title="취소"
          aria-label="취소"
        >
          <X size={12} />
        </button>
      </div>

      <div className="dw-ci-picker-field">
        <Search size={14} className="dw-ci-picker-icon" />
        <input
          ref={inputRef}
          type="text"
          className="dw-ci-picker-input"
          placeholder={current ? `현재: ${current} · 새 심볼 입력` : "AAPL · 애플 · 005930"}
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); confirm(); }
            else if (e.key === "Escape") { e.preventDefault(); onCancel(); }
            else if (e.key === "ArrowDown") {
              e.preventDefault();
              setFocusIdx((i) => Math.min(i + 1, Math.max(0, items.length - 1)));
            }
            else if (e.key === "ArrowUp") {
              e.preventDefault();
              setFocusIdx((i) => Math.max(i - 1, 0));
            }
          }}
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      <div className="dw-ci-picker-list">
        {loading && items.length === 0 ? (
          <div className="dw-ci-picker-empty">검색 중…</div>
        ) : items.length === 0 ? (
          <div className="dw-ci-picker-empty">
            {query.trim() ? "매칭 종목 없음" : "기업명·심볼 입력"}
          </div>
        ) : (
          items.map((it, i) => (
            <button
              key={it.symbol}
              type="button"
              className={`dw-ci-picker-item ${i === focusIdx ? "is-focus" : ""}`}
              onMouseEnter={() => setFocusIdx(i)}
              onMouseDown={(e) => { e.preventDefault(); confirm(it.symbol); }}
            >
              <span className="dw-ci-picker-sym">{it.symbol}</span>
              <span className="dw-ci-picker-name">{it.name || ""}</span>
            </button>
          ))
        )}
      </div>

      <div className="dw-ci-picker-hint">
        ↑↓ 이동 · Enter 확정 · Esc 취소
      </div>
    </div>
  );
}

/** 52주 세로 bar — $high 위 · bar 중앙(세로) · $low 아래 · marker 현재 위치 */
function Week52Bar({
  low,
  high,
  current,
}: {
  low: number | null;
  high: number | null;
  current: number | null;
}) {
  if (low == null || high == null || current == null || high <= low) return null;
  const pct = Math.max(0, Math.min(100, ((current - low) / (high - low)) * 100));
  return (
    <div className="dw-ci-52v" aria-label={`52주 범위: ${low}~${high}, 현재 ${pct.toFixed(0)}%`}>
      <span className="dw-ci-52v-high">${high.toFixed(high < 10 ? 2 : 0)}</span>
      <div className="dw-ci-52v-track">
        <span className="dw-ci-52v-marker" style={{ bottom: `${pct}%` }} />
      </div>
      <span className="dw-ci-52v-low">${low.toFixed(low < 10 ? 2 : 0)}</span>
      <span className="dw-ci-52v-label">52주</span>
    </div>
  );
}

export function CompanyInfo({ options, onOptionsChange }: WidgetProps) {
  const rawSymbol = typeof options?.symbol === "string" ? options.symbol : "";
  const symbol = rawSymbol.trim().toUpperCase();

  // ⚠️ Hooks — 모두 early return 이전에 호출 (React 규칙)
  const [picking, setPicking] = useState(false);
  const [data, setData] = useState<CompanyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!symbol) {
      setData(null);
      setLoading(false);
      setError("심볼 없음");
      return;
    }
    // 심볼 바뀌면 이전 데이터 즉시 초기화 → 스켈레톤 표시 (이전 종목 가격이
    // 잠깐 보이는 '깜빡임' 방지)
    setData(null);
    setError(null);
    setLoading(true);
    async function load() {
      try {
        const res = await fetch(`/api/ticker/company/${encodeURIComponent(symbol)}`, {
          credentials: "include",
        });
        if (!res.ok) {
          if (mountedRef.current) {
            setError(res.status === 404 ? "종목 없음" : "로드 실패");
            setLoading(false);
          }
          return;
        }
        const d = (await res.json()) as CompanyData;
        if (mountedRef.current) {
          setData(d);
          setError(null);
          setLoading(false);
        }
      } catch {
        if (mountedRef.current) {
          setError("네트워크 오류");
          setLoading(false);
        }
      }
    }
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(t);
    };
  }, [symbol]);

  // 렌더 분기 — picking 모드는 카드 자체가 TickerPicker 로 전환
  if (picking) {
    return (
      <TickerPicker
        current={symbol}
        onSelect={(sym) => {
          if (sym !== symbol) {
            onOptionsChange?.({ ...(options ?? {}), symbol: sym });
          }
          setPicking(false);
        }}
        onCancel={() => setPicking(false)}
      />
    );
  }

  const pct = data?.changePercent ?? null;
  const up = pct != null && pct > 0;
  const down = pct != null && pct < 0;
  const flat = pct != null && pct === 0;

  return (
    <div className="dw-body dw-ci">
      {error ? (
        <div className="dw-ci-error">
          <div className="dw-ci-error-ticker">{symbol || "—"}</div>
          <div className="dw-ci-error-msg">{error}</div>
        </div>
      ) : (
        <>
          {/* 헤더 — [로고] [티커(+변경버튼) / 회사명] */}
          <div className="dw-ci-header">
            {data?.logoUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={data.logoUrl} alt="" className="dw-ci-logo" />
            ) : (
              <div className="dw-ci-logo dw-ci-logo-placeholder">
                {symbol.slice(0, 2)}
              </div>
            )}
            <div className="dw-ci-id-wrap">
              {/* 사용자 결정 #5: 기업명(한국 이름 우선) primary, 티커는 우측 secondary */}
              <div className="dw-ci-ticker-row">
                <Link
                  href={`/analyze/${encodeURIComponent(symbol)}`}
                  className="dw-ci-ticker-link dw-no-drag"
                  title={`${symbol} 상세 분석 페이지로`}
                >
                  {loading && !data ? (
                    <span className="bs-skel" style={{ width: 100, height: 14, display: "inline-block" }} />
                  ) : (
                    <span className="dw-ci-name">
                      {koreanCompanyName(symbol, data?.name)}
                    </span>
                  )}
                </Link>
                <button
                  type="button"
                  className="dw-ci-swap-btn dw-no-drag"
                  onClick={() => setPicking(true)}
                  title="다른 기업으로 변경"
                >
                  <Repeat2 size={11} />
                  <span>종목 변경</span>
                </button>
              </div>
              <div className="dw-ci-ticker">{symbol}</div>
            </div>
          </div>

          {/* 메인 row — [가격·변동률] [스파크라인] [52주 세로 bar] */}
          <div className="dw-ci-main">
            <div className="dw-ci-price-block">
              <div className="dw-ci-price-line">
                {loading && !data ? (
                  <span className="bs-skel" style={{ width: 100, height: 22, display: "block" }} />
                ) : (
                  <span className="dw-ci-price">
                    {data?.price != null ? `$${formatPrice(data.price)}` : "—"}
                  </span>
                )}
              </div>
              <div className={`dw-ci-change ${up ? "up" : ""} ${down ? "down" : ""} ${flat ? "flat" : ""}`}>
                {up && <TrendingUp size={13} />}
                {down && <TrendingDown size={13} />}
                {flat && <Minus size={13} />}
                <span>{pct != null ? `${up ? "+" : ""}${(pct * 100).toFixed(2)}%` : "—"}</span>
              </div>
            </div>
            <div className="dw-ci-spark-wrap">
              {data?.sparkline && data.sparkline.length >= 2 && (
                <Sparkline values={data.sparkline} up={up} />
              )}
            </div>
            <Week52Bar
              low={data?.low52w ?? null}
              high={data?.high52w ?? null}
              current={data?.price ?? null}
            />
          </div>

          {/* AI 큐레이션된 이슈 브리핑 */}
          <Insights insights={data?.brief?.insights ?? []} loading={loading && !data} />

          {/* 상세 페이지 진입 CTA */}
          <Link
            href={`/analyze/${encodeURIComponent(symbol)}`}
            className="dw-ci-detail-btn dw-no-drag"
          >
            <span>더 자세히 보기</span>
            <ArrowUpRight size={12} />
          </Link>
        </>
      )}
    </div>
  );
}

export const companyInfoMeta: WidgetMeta = {
  type: "company-info",
  name: "기업 정보",
  description: "심볼 · 가격 · 차트 · 52주 · 뉴스 · 메타",
  icon: "🏢",
  category: "personal",
  defaultSize: { w: 4, h: 5 },
  availableSizes: [
    { w: 3, h: 5 },
    { w: 4, h: 5 },
    { w: 6, h: 5 },
  ],
  minW: 3,
  maxW: 12,
  minH: 4,
  maxH: 7,
  component: CompanyInfo,
};
